import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Upload, 
  Plus, 
  Trash2, 
  ChevronUp,
  ChevronDown,
  FileUp,
  Sparkles,
  FileText,
  Magnet,
  BarChart3,
  Crown,
  Youtube
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FieldType = "email" | "phone" | "full_name" | "first_name" | "job_title" | "company" | "custom_text" | "custom_select";

interface FormField {
  id?: string;
  field_type: FieldType;
  label: string;
  placeholder: string;
  is_required: boolean;
  options: string[];
  sort_order: number;
}

interface FormData {
  title: string;
  slug: string;
  description: string;
  logo_url: string;
  color_primary: string;
  color_secondary: string;
  color_accent: string;
  color_text_light: string;
  color_text_dark: string;
  ai_example_questions: string[];
  ai_question_count: number;
  ai_final_prompt: string;
  ai_output_word_limit: number;
  thank_you_message: string;
  vsl_youtube_url: string;
  use_lead_history: boolean;
  is_active: boolean;
  ga_pixel_id: string;
  meta_pixel_id: string;
  reward_type: "ai" | "file";
  reward_description: string;
  reward_file_url: string;
}

const fieldTypeLabels: Record<FieldType, string> = {
  email: "Email",
  phone: "Telefone",
  full_name: "Nome completo",
  first_name: "Primeiro nome",
  job_title: "Cargo",
  company: "Empresa",
  custom_text: "Texto personalizado",
  custom_select: "Seleção personalizada",
};

export default function FormBuilder() {
  const { formId } = useParams();
  const isEditing = !!formId;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { role, isLoading: planLoading } = useUserPlan();
  const isPremium = role === "admin" || role === "pro";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    title: "",
    slug: "",
    description: "",
    logo_url: "",
    color_primary: "#6366f1",
    color_secondary: "#f1f5f9",
    color_accent: "#10b981",
    color_text_light: "#ffffff",
    color_text_dark: "#1e293b",
    ai_example_questions: [""],
    ai_question_count: 5,
    ai_final_prompt: "",
    ai_output_word_limit: 2000,
    thank_you_message: "Obrigado por completar o formulário! Seu PDF personalizado está pronto.",
    vsl_youtube_url: "",
    use_lead_history: false,
    is_active: true,
    ga_pixel_id: "",
    meta_pixel_id: "",
    reward_type: "ai",
    reward_description: "",
    reward_file_url: "",
  });
  const [userFormCount, setUserFormCount] = useState<number | null>(null);
  const [uploadingReward, setUploadingReward] = useState(false);

  const [fields, setFields] = useState<FormField[]>([
    { field_type: "email", label: "Email", placeholder: "seu@email.com", is_required: true, options: [], sort_order: 0 },
  ]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch user's form count for VSL field eligibility
  useEffect(() => {
    const fetchUserFormCount = async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;
      
      const { count } = await supabase
        .from("forms")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userData.user.id);
      
      setUserFormCount(count ?? 0);
    };
    
    if (isAuthenticated) {
      fetchUserFormCount();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isEditing && isAuthenticated) {
      fetchForm();
    }
  }, [formId, isAuthenticated]);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const { data: form, error } = await supabase
        .from("forms")
        .select("*")
        .eq("id", formId)
        .single();

      if (error) throw error;

      setFormData({
        title: form.title,
        slug: form.slug,
        description: form.description || "",
        logo_url: form.logo_url || "",
        color_primary: form.color_primary || "#6366f1",
        color_secondary: form.color_secondary || "#f1f5f9",
        color_accent: form.color_accent || "#10b981",
        color_text_light: form.color_text_light || "#ffffff",
        color_text_dark: form.color_text_dark || "#1e293b",
        ai_example_questions: form.ai_example_questions || [""],
        ai_question_count: form.ai_question_count || 5,
        ai_final_prompt: form.ai_final_prompt || "",
        ai_output_word_limit: form.ai_output_word_limit || 2000,
        thank_you_message: form.thank_you_message || "",
        vsl_youtube_url: (form as any).vsl_youtube_url || "",
        use_lead_history: form.use_lead_history || false,
        is_active: form.is_active,
        ga_pixel_id: form.ga_pixel_id || "",
        meta_pixel_id: form.meta_pixel_id || "",
        reward_type: (form.reward_type as "ai" | "file") || "ai",
        reward_description: form.reward_description || "",
        reward_file_url: form.reward_file_url || "",
      });

      const { data: formFields, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formId)
        .order("sort_order");

      if (fieldsError) throw fieldsError;

      if (formFields && formFields.length > 0) {
        setFields(formFields.map(f => ({
          id: f.id,
          field_type: f.field_type as FieldType,
          label: f.label,
          placeholder: f.placeholder || "",
          is_required: f.is_required ?? true,
          options: f.options || [],
          sort_order: f.sort_order,
        })));
      }
    } catch (error) {
      console.error("Error fetching form:", error);
      toast({
        title: "Erro ao carregar formulário",
        variant: "destructive",
      });
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: isEditing ? prev.slug : generateSlug(value),
    }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione uma imagem.",
        variant: "destructive",
      });
      return;
    }

    setUploadingLogo(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("User not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `${userData.user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("logos")
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, logo_url: publicUrl }));
      toast({ title: "Logo enviado com sucesso!" });
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast({
        title: "Erro ao enviar logo",
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const addField = () => {
    setFields(prev => [
      ...prev,
      {
        field_type: "custom_text",
        label: "Novo campo",
        placeholder: "",
        is_required: false,
        options: [],
        sort_order: prev.length,
      },
    ]);
  };

  const removeField = (index: number) => {
    if (fields[index].field_type === "email") {
      toast({
        title: "Campo obrigatório",
        description: "O campo de email não pode ser removido.",
        variant: "destructive",
      });
      return;
    }
    setFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const moveFieldUp = (index: number) => {
    if (index === 0) return;
    setFields(prev => {
      const newFields = [...prev];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      return newFields;
    });
  };

  const moveFieldDown = (index: number) => {
    if (index === fields.length - 1) return;
    setFields(prev => {
      const newFields = [...prev];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      return newFields;
    });
  };

  const addExampleQuestion = () => {
    setFormData(prev => ({
      ...prev,
      ai_example_questions: [...prev.ai_example_questions, ""],
    }));
  };

  const updateExampleQuestion = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      ai_example_questions: prev.ai_example_questions.map((q, i) => i === index ? value : q),
    }));
  };

  const removeExampleQuestion = (index: number) => {
    if (formData.ai_example_questions.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      ai_example_questions: prev.ai_example_questions.filter((_, i) => i !== index),
    }));
  };

  const handleRewardFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Max 50MB
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O arquivo deve ter no máximo 50MB.",
        variant: "destructive",
      });
      return;
    }

    setUploadingReward(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("User not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `${userData.user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("rewards")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("rewards")
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, reward_file_url: publicUrl }));
      toast({ title: "Arquivo de recompensa enviado!" });
    } catch (error) {
      console.error("Error uploading reward file:", error);
      toast({
        title: "Erro ao enviar arquivo",
        variant: "destructive",
      });
    } finally {
      setUploadingReward(false);
    }
  };

  const removeRewardFile = () => {
    setFormData(prev => ({ ...prev, reward_file_url: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, informe um título para o formulário.",
        variant: "destructive",
      });
      return;
    }

    // Validate based on reward type
    if (formData.reward_type === "ai" && !formData.ai_final_prompt.trim()) {
      toast({
        title: "Prompt final obrigatório",
        description: "Por favor, descreva o que deve ser gerado no PDF.",
        variant: "destructive",
      });
      return;
    }

    if (formData.reward_type === "file") {
      if (!formData.reward_file_url) {
        toast({
          title: "Arquivo obrigatório",
          description: "Por favor, faça upload do arquivo de recompensa.",
          variant: "destructive",
        });
        return;
      }
      if (!formData.reward_description.trim()) {
        toast({
          title: "Descrição obrigatória",
          description: "Por favor, descreva a recompensa para guiar o formulário.",
          variant: "destructive",
        });
        return;
      }
    }

    // AI reward requires PRO to publish - free users can only save as inactive
    const canPublishAiReward = isPremium || formData.reward_type === "file";
    const finalIsActive = canPublishAiReward ? formData.is_active : false;
    
    if (formData.reward_type === "ai" && !isPremium && formData.is_active) {
      toast({
        title: "Plano Pro necessário",
        description: "Formulários com recompensa gerada por IA precisam do plano Pro para serem publicados. O formulário será salvo como inativo.",
      });
    }

    setSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("User not authenticated");

      // Determine if VSL field can be saved (first form for free users OR pro users)
      const canUseVsl = isPremium || (userFormCount !== null && userFormCount === 0 && !isEditing);
      
      const formPayload = {
        user_id: userData.user.id,
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || null,
        logo_url: formData.logo_url || null,
        color_primary: formData.color_primary,
        color_secondary: formData.color_secondary,
        color_accent: formData.color_accent,
        color_text_light: formData.color_text_light,
        color_text_dark: formData.color_text_dark,
        ai_example_questions: formData.ai_example_questions.filter(q => q.trim()),
        ai_question_count: formData.ai_question_count,
        ai_final_prompt: formData.ai_final_prompt.trim(),
        ai_output_word_limit: formData.ai_output_word_limit,
        thank_you_message: formData.thank_you_message.trim(),
        vsl_youtube_url: canUseVsl && formData.vsl_youtube_url.trim() ? formData.vsl_youtube_url.trim() : null,
        use_lead_history: formData.use_lead_history,
        is_active: finalIsActive,
        ga_pixel_id: isPremium && formData.ga_pixel_id.trim() ? formData.ga_pixel_id.trim() : null,
        meta_pixel_id: isPremium && formData.meta_pixel_id.trim() ? formData.meta_pixel_id.trim() : null,
        reward_type: formData.reward_type,
        reward_description: formData.reward_type === "file" ? formData.reward_description.trim() : null,
        reward_file_url: formData.reward_type === "file" ? formData.reward_file_url : null,
      };

      let savedFormId = formId;

      if (isEditing) {
        const { error } = await supabase
          .from("forms")
          .update(formPayload)
          .eq("id", formId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("forms")
          .insert(formPayload)
          .select()
          .single();

        if (error) throw error;
        savedFormId = data.id;
      }

      // Handle fields
      if (isEditing) {
        await supabase.from("form_fields").delete().eq("form_id", savedFormId);
      }

      const fieldsPayload = fields.map((f, i) => ({
        form_id: savedFormId,
        field_type: f.field_type,
        label: f.label,
        placeholder: f.placeholder || null,
        is_required: f.is_required,
        options: f.options,
        sort_order: i,
      }));

      const { error: fieldsError } = await supabase
        .from("form_fields")
        .insert(fieldsPayload);

      if (fieldsError) throw fieldsError;

      toast({
        title: isEditing ? "Formulário atualizado!" : "Formulário criado!",
        description: `Seu formulário está disponível em /f/${formData.slug}`,
      });

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error saving form:", error);
      if (error.code === "23505") {
        toast({
          title: "Slug já existe",
          description: "Já existe um formulário com este slug. Escolha outro título.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao salvar",
          description: error.message || "Não foi possível salvar o formulário.",
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading || planLoading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Magnet className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold hidden sm:block">
                {isEditing ? "Editar Formulário" : "Novo Formulário"}
              </span>
            </div>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-gradient-primary hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvar
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto px-4 py-8 pb-[100px] max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure o título e descrição do seu formulário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Ex: Análise de Perfil Profissional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">/f/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="meu-formulario"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva brevemente o propósito deste formulário..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Formulário ativo</Label>
              </div>
            </CardContent>
          </Card>

          {/* Visual Identity */}
          <Card>
            <CardHeader>
              <CardTitle>Identidade Visual</CardTitle>
              <CardDescription>
                Personalize a aparência do formulário para seus leads
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  {formData.logo_url && (
                    <img
                      src={formData.logo_url}
                      alt="Logo"
                      className="w-16 h-16 object-contain rounded-lg border border-border"
                    />
                  )}
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-border hover:border-primary transition-colors">
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span className="text-sm">
                        {formData.logo_url ? "Trocar logo" : "Enviar logo"}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                  </label>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-5">
                {[
                  { key: "color_primary", label: "Primária" },
                  { key: "color_secondary", label: "Secundária" },
                  { key: "color_accent", label: "Destaque" },
                  { key: "color_text_light", label: "Texto claro" },
                  { key: "color_text_dark", label: "Texto escuro" },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{label}</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id={key}
                        value={formData[key as keyof FormData] as string}
                        onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-10 h-10 rounded-lg border border-border cursor-pointer"
                      />
                      <Input
                        value={formData[key as keyof FormData] as string}
                        onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                        className="flex-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fixed Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Campos Fixos</CardTitle>
              <CardDescription>
                Configure os campos que serão exibidos antes das perguntas da IA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {fields.map((field, index) => (
                <div key={index} className="flex flex-col sm:flex-row items-start gap-3 p-4 rounded-lg border border-border bg-card">
                  <div className="flex sm:flex-col gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveFieldUp(index)}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveFieldDown(index)}
                      disabled={index === fields.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1 w-full grid gap-3 sm:grid-cols-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo</Label>
                      <Select
                        value={field.field_type}
                        onValueChange={(value) => updateField(index, { field_type: value as FieldType })}
                        disabled={field.field_type === "email"}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(fieldTypeLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={field.label}
                        onChange={(e) => updateField(index, { label: e.target.value })}
                        placeholder="Label do campo"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Placeholder</Label>
                      <Input
                        value={field.placeholder}
                        onChange={(e) => updateField(index, { placeholder: e.target.value })}
                        placeholder="Texto de exemplo"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex items-center gap-2 flex-1">
                        <Switch
                          checked={field.is_required}
                          onCheckedChange={(checked) => updateField(index, { is_required: checked })}
                          disabled={field.field_type === "email"}
                        />
                        <Label className="text-xs">Obrigatório</Label>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeField(index)}
                        disabled={field.field_type === "email"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addField}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar campo
              </Button>
            </CardContent>
          </Card>

          {/* Reward Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="flex items-center gap-2">
                  {formData.reward_type === "ai" ? (
                    <Sparkles className="h-5 w-5" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                  Recompensa do Formulário
                </CardTitle>
              </div>
              <CardDescription>
                Configure o que o lead receberá ao completar o formulário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Reward Type Selector - available for all users */}
              <div className="space-y-3">
                <Label>Tipo de Recompensa</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, reward_type: "ai" }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.reward_type === "ai"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${formData.reward_type === "ai" ? "bg-primary/10" : "bg-muted"}`}>
                        <Sparkles className={`h-5 w-5 ${formData.reward_type === "ai" ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Gerado por IA</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                          <Crown className="h-3 w-3" />
                          Pro
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      A IA gera um PDF personalizado com base nas respostas
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, reward_type: "file" }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.reward_type === "file"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${formData.reward_type === "file" ? "bg-primary/10" : "bg-muted"}`}>
                        <FileUp className={`h-5 w-5 ${formData.reward_type === "file" ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <span className="font-medium">Arquivo Próprio</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Faça upload de um arquivo (PDF, e-book, etc.)
                    </p>
                  </button>
                </div>
              </div>

              {/* Pro upgrade notice for AI reward */}
              {formData.reward_type === "ai" && !isPremium && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <Crown className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800">
                      Requer plano Pro para publicar
                    </p>
                    <p className="text-sm text-amber-700">
                      Você pode configurar tudo, mas o formulário só poderá ser publicado com o plano Pro ativo.
                    </p>
                    <Link to="/upgrade" className="inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2">
                      Fazer upgrade agora
                    </Link>
                  </div>
                </div>
              )}

              {/* AI Configuration - shown when reward_type is "ai" */}
              {formData.reward_type === "ai" && (
                <>
                  <div className="space-y-4">
                    <Label>Exemplos de Perguntas</Label>
                    <p className="text-sm text-muted-foreground">
                      Forneça exemplos de perguntas para a IA se basear no estilo e tema
                    </p>
                    {formData.ai_example_questions.map((question, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={question}
                          onChange={(e) => updateExampleQuestion(index, e.target.value)}
                          placeholder="Ex: Qual seu maior desafio profissional atualmente?"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeExampleQuestion(index)}
                          disabled={formData.ai_example_questions.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addExampleQuestion}>
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar exemplo
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai_question_count">Quantidade de Perguntas da IA</Label>
                    <Input
                      id="ai_question_count"
                      type="number"
                      min={1}
                      max={20}
                      value={formData.ai_question_count}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_question_count: parseInt(e.target.value) || 5 }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai_output_word_limit">Limite de Palavras do Resultado</Label>
                    <p className="text-sm text-muted-foreground">
                      Controla o tamanho máximo do documento gerado (afeta o custo por lead)
                    </p>
                    <Input
                      id="ai_output_word_limit"
                      type="number"
                      min={500}
                      max={10000}
                      step={500}
                      value={formData.ai_output_word_limit}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_output_word_limit: parseInt(e.target.value) || 2000 }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Sugestões: 1000 (resumo), 2000 (padrão), 5000 (detalhado), 10000 (extenso)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai_final_prompt">Prompt Final (O que gerar no PDF) *</Label>
                    <Textarea
                      id="ai_final_prompt"
                      value={formData.ai_final_prompt}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_final_prompt: e.target.value }))}
                      placeholder="Ex: Gere uma análise detalhada do perfil profissional do lead, incluindo pontos fortes, áreas de melhoria e recomendações personalizadas..."
                      rows={4}
                    />
                  </div>
                </>
              )}

              {/* File Upload Configuration - shown when reward_type is "file" - available for all users */}
              {formData.reward_type === "file" && (
                <>
                  <div className="space-y-3">
                    <Label>Descrição da Recompensa *</Label>
                    <p className="text-sm text-muted-foreground">
                      Descreva o que é a recompensa para guiar as perguntas do formulário
                    </p>
                    <Textarea
                      value={formData.reward_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, reward_description: e.target.value }))}
                      placeholder="Ex: E-book com 50 dicas de produtividade para empreendedores, incluindo técnicas de gestão de tempo, organização de tarefas e automação de processos..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Arquivo de Recompensa *</Label>
                    <p className="text-sm text-muted-foreground">
                      Faça upload do arquivo que o lead vai baixar (PDF, e-book, template, etc.)
                    </p>
                    
                    {formData.reward_file_url ? (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
                        <FileText className="h-8 w-8 text-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">Arquivo enviado</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {formData.reward_file_url.split('/').pop()}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={removeRewardFile}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          onChange={handleRewardFileUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingReward}
                        />
                        <div className="flex items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-all">
                          {uploadingReward ? (
                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          ) : (
                            <FileUp className="h-5 w-5 text-primary" />
                          )}
                          <span className="text-sm font-medium">
                            {uploadingReward ? "Enviando..." : "Clique para enviar arquivo"}
                          </span>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Máximo 50MB. Formatos sugeridos: PDF, DOCX, XLSX, ZIP
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai_question_count_file">Quantidade de Perguntas da IA</Label>
                    <p className="text-sm text-muted-foreground">
                      A IA vai fazer perguntas contextuais baseadas na descrição da recompensa
                    </p>
                    <Input
                      id="ai_question_count_file"
                      type="number"
                      min={0}
                      max={20}
                      value={formData.ai_question_count}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_question_count: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </>
              )}

              {/* Common settings */}
              <div className="space-y-2">
                <Label htmlFor="thank_you_message">Mensagem de Agradecimento</Label>
                <Textarea
                  id="thank_you_message"
                  value={formData.thank_you_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, thank_you_message: e.target.value }))}
                  placeholder="Mensagem exibida após completar o formulário..."
                  rows={2}
                />
              </div>

              {/* VSL YouTube Video - First form for free users OR Pro users */}
              {(isPremium || (userFormCount !== null && userFormCount === 0 && !isEditing) || (isEditing && formData.vsl_youtube_url)) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="vsl_youtube_url" className="flex items-center gap-2">
                      <Youtube className="h-4 w-4 text-red-500" />
                      Vídeo de Vendas (VSL)
                    </Label>
                    {!isPremium && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                        Grátis no 1º formulário
                      </span>
                    )}
                  </div>
                  <Input
                    id="vsl_youtube_url"
                    value={formData.vsl_youtube_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, vsl_youtube_url: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/... — Crie um vídeo curto (2-5 min) explicando como usar a recompensa e qual o próximo passo no seu funil de vendas"
                  />
                  <p className="text-xs text-muted-foreground">
                    💡 <strong>Dica para sua VSL:</strong> Grave um vídeo de 2 a 5 minutos explicando: 1) Como o lead deve usar a recompensa que acabou de receber; 2) Qual é o próximo passo que você quer que ele dê no seu funil (agendar uma call, comprar um produto, etc); 3) Crie urgência mostrando o valor da sua oferta.
                  </p>
                </div>
              )}

              {!isPremium && userFormCount !== null && userFormCount > 0 && !isEditing && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border">
                  <Youtube className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      Vídeo de Vendas (VSL)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      O recurso de VSL está disponível apenas no primeiro formulário para contas gratuitas. Faça upgrade para o plano Pro para usar em todos os formulários.
                    </p>
                    <Link to="/upgrade" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-2">
                      <Crown className="h-3 w-3" />
                      Fazer upgrade agora
                    </Link>
                  </div>
                </div>
              )}

              {formData.reward_type === "ai" && (
                <div className="flex items-center gap-3">
                  <Switch
                    id="use_lead_history"
                    checked={formData.use_lead_history}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, use_lead_history: checked }))}
                  />
                  <div>
                    <Label htmlFor="use_lead_history">Usar histórico do lead</Label>
                    <p className="text-xs text-muted-foreground">
                      Se o email já existe na base, a IA usa o histórico para personalizar
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tracking & Analytics - Pro Only */}
          <Card className={!isPremium ? "opacity-60" : ""}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Rastreamento & Analytics
                </CardTitle>
                {!isPremium && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                    <Crown className="h-3 w-3" />
                    Pro
                  </span>
                )}
              </div>
              <CardDescription>
                {isPremium 
                  ? "Configure pixels de rastreamento para acompanhar conversões"
                  : "Faça upgrade para o plano Pro para usar pixels de rastreamento"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="ga_pixel_id">Google Analytics (GA4)</Label>
                  <Input
                    id="ga_pixel_id"
                    value={formData.ga_pixel_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, ga_pixel_id: e.target.value }))}
                    placeholder="G-XXXXXXXXXX"
                    disabled={!isPremium}
                  />
                  <p className="text-xs text-muted-foreground">
                    ID de medição do GA4 (ex: G-ABC123XYZ)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta_pixel_id">Meta Pixel (Facebook)</Label>
                  <Input
                    id="meta_pixel_id"
                    value={formData.meta_pixel_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta_pixel_id: e.target.value }))}
                    placeholder="1234567890"
                    disabled={!isPremium}
                  />
                  <p className="text-xs text-muted-foreground">
                    ID do Pixel do Meta/Facebook
                  </p>
                </div>
              </div>
              {!isPremium && (
                <div className="p-3 rounded-lg bg-muted/50 border border-dashed">
                  <p className="text-sm text-muted-foreground text-center">
                    <Link to="/upgrade" className="text-primary hover:underline font-medium">
                      Upgrade para Pro
                    </Link>
                    {" "}para desbloquear rastreamento avançado
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
}
